import { useEffect, useRef, useState } from 'react';

import { keycloak } from './keycloak';

let initPromise: Promise<boolean> | null = null;
const AUTH_CALLBACK_PARAM_KEYS = [
  'state',
  'session_state',
  'code',
  'iss',
  'kc_action',
  'kc_action_status',
  'error',
  'error_description',
  'error_uri',
  'access_token',
  'id_token',
  'token_type',
  'expires_in',
] as const;

const inIframe = (() => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
})();

function getHashParams() {
  return new URLSearchParams(window.location.hash.replace(/^#/, ''));
}

function stripAuthCallbackParamsFromUrl(preserveAdapterCallback = false) {
  const url = new URL(window.location.href);
  let changed = false;

  for (const key of AUTH_CALLBACK_PARAM_KEYS) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  }

  if (url.hash) {
    const hashParams = getHashParams();
    const hasAdapterCallback =
      hashParams.has('state') && (hashParams.has('code') || hashParams.has('error'));
    let hashChanged = false;

    // Preserve the adapter's state-bearing fragment until it validates the callback.
    if (!preserveAdapterCallback || !hasAdapterCallback) {
      for (const key of AUTH_CALLBACK_PARAM_KEYS) {
        if (hashParams.has(key)) {
          hashParams.delete(key);
          hashChanged = true;
          changed = true;
        }
      }
    }

    if (hashChanged) {
      const nextHash = hashParams.toString();
      url.hash = nextHash ? `#${nextHash}` : '';
    }
  }

  if (changed) {
    window.history.replaceState(window.history.state, '', url.toString());
  }
}

function initKeycloak() {
  const params = new URLSearchParams(window.location.search);
  const hashParams = getHashParams();
  const urlToken = params.get('token');
  const authCode = params.get('code') || hashParams.get('code');

  if (!initPromise && !urlToken) {
    const processingKey = 'keycloak_processing_auth';

    // Invitation required actions can return query parameters without adapter state.
    // Remove them before login-required can reuse the current URL as redirect_uri.
    stripAuthCallbackParamsFromUrl(true);

    const initCall = keycloak
      .init({
        onLoad: inIframe ? 'check-sso' : 'login-required',
        pkceMethod: 'S256',
        checkLoginIframe: false,
        silentCheckSsoRedirectUri: inIframe
          ? undefined
          : `${window.location.origin}/silent-check-sso.html`,
        silentCheckSsoFallback: false,
        flow: 'standard',
        responseMode: 'fragment',
      })
      .then((auth) => {
        stripAuthCallbackParamsFromUrl();

        if (auth && inIframe) {
          sessionStorage.removeItem(processingKey);
        }

        return auth;
      })
      .catch(() => {
        if (inIframe) {
          sessionStorage.removeItem(processingKey);
        }
        return false;
      });

    const timeoutMs = authCode ? 10000 : inIframe ? 2000 : 5000;
    const timeout = new Promise<boolean>((resolve) => {
      setTimeout(() => {
        if (inIframe) {
          sessionStorage.removeItem(processingKey);
        }
        resolve(false);
      }, timeoutMs);
    });

    initPromise = Promise.race([initCall, timeout]);
  }

  return initPromise;
}

export function useAuth() {
  const [authenticated, setAuthenticated] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const refreshTimer = useRef<number | null>(null);
  const loginWindowRef = useRef<Window | null>(null);

  const setupTokenRefresh = () => {
    if (refreshTimer.current) {
      clearInterval(refreshTimer.current);
    }

    const tick = async () => {
      if (document.hidden || !keycloak.authenticated) return;

      try {
        await keycloak.updateToken(60);
      } catch {
        if (!inIframe && !keycloak.authenticated) {
          keycloak.login();
        } else if (inIframe) {
          setAuthenticated(false);
        }
      }
    };

    refreshTimer.current = window.setInterval(tick, 30_000);

    const onFocus = () => {
      if (!keycloak.authenticated) return;
      keycloak.updateToken(60).catch(() => {
        if (!inIframe && !keycloak.authenticated) {
          keycloak.login();
        } else if (inIframe) {
          setAuthenticated(false);
        }
      });
    };

    window.addEventListener('focus', onFocus);

    keycloak.onTokenExpired = () =>
      keycloak.updateToken(60).catch(() => {
        if (!inIframe && !keycloak.authenticated) {
          keycloak.login();
        } else if (inIframe) {
          setAuthenticated(false);
        }
      });

    return () => {
      if (refreshTimer.current) clearInterval(refreshTimer.current);
      window.removeEventListener('focus', onFocus);
    };
  };

  useEffect(() => {
    if (!inIframe) return;

    const searchParams = new URLSearchParams(window.location.search);
    const hashParams = getHashParams();
    const hasAuthCode =
      (searchParams.has('code') && searchParams.has('state')) ||
      (hashParams.has('code') && hashParams.has('state'));
    const processingKey = 'keycloak_processing_auth';
    const isCurrentlyProcessing = sessionStorage.getItem(processingKey) === 'true';

    if (hasAuthCode || isCurrentlyProcessing) return;

    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (sessionStorage.getItem(processingKey) === 'true') return;

      if (event.data?.type === 'keycloak-tokens') {
        const { token, refreshToken, idToken, tokenParsed } = event.data;

        sessionStorage.setItem(processingKey, 'true');

        if (loginWindowRef.current && !loginWindowRef.current.closed) {
          loginWindowRef.current.close();
        }

        try {
          keycloak.token = token;
          keycloak.refreshToken = refreshToken;
          keycloak.idToken = idToken;
          keycloak.tokenParsed = tokenParsed;
          keycloak.authenticated = true;

          setAuthenticated(true);
          setIsAuthenticating(false);
          setupTokenRefresh();
          sessionStorage.removeItem(processingKey);
        } catch {
          sessionStorage.removeItem(processingKey);
        }
      }

      if (event.data?.type === 'keycloak-auth-error') {
        if (loginWindowRef.current && !loginWindowRef.current.closed) {
          loginWindowRef.current.close();
        }
        sessionStorage.removeItem(processingKey);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (!inIframe) return;

    const handleTokenExpired = () => {
      setAuthenticated(false);
      setIsAuthenticating(false);
    };

    window.addEventListener('keycloak-token-expired', handleTokenExpired);
    return () => window.removeEventListener('keycloak-token-expired', handleTokenExpired);
  }, []);

  useEffect(() => {
    let mounted = true;

    initKeycloak()
      ?.then(async (auth) => {
        if (!mounted) return;

        if (!auth) {
          const urlToken = new URLSearchParams(window.location.search).get('token');

          if (inIframe && !urlToken) {
            setAuthenticated(false);
            setIsAuthenticating(false);
            return;
          }

          if (!urlToken) {
            setIsAuthenticating(false);
            setTimeout(() => keycloak.login(), 0);
            return;
          }
        }

        setAuthenticated(!!auth);
        setIsAuthenticating(false);

        if (auth && inIframe) {
          sessionStorage.removeItem('keycloak_processing_auth');
        }

        if (auth) {
          setupTokenRefresh();

          return () => {
            mounted = false;
            if (refreshTimer.current) clearInterval(refreshTimer.current);
          };
        }
      })
      .catch(() => {
        setIsAuthenticating(false);
        setAuthenticated(false);

        if (inIframe) {
          sessionStorage.removeItem('keycloak_processing_auth');
        }
      });
  }, []);

  const shouldShowLoginCta = inIframe && !authenticated && !isAuthenticating;

  const login = async () => {
    if (inIframe) {
      const width = 500;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const loginUrl = await keycloak.createLoginUrl({
        redirectUri: window.location.origin + '/auth-callback.html',
      });

      loginWindowRef.current = window.open(
        loginUrl,
        'keycloak-login',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
      );
    } else {
      keycloak.login();
    }
  };

  return {
    token: keycloak.token,
    authenticated,
    isAuthenticating,
    shouldShowLoginCta,
    login,
    logout: () => keycloak.logout({ redirectUri: 'https://www.sensorinnovation.no' }),
  };
}
